import React, { createContext, useContext, useEffect, useMemo, useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  LANGUAGE_KEY,
  LEGACY_LANGUAGE_KEY,
  normalizeLanguage,
  readLanguagePreference,
  writeLanguagePreference,
} from '../utils/languagePreferences';
import {
  LANGUAGE_ACCOUNT_LOCK_CUSTOM_EVENT,
  LANGUAGE_ACCOUNT_LOCK_REASON_MOCK_EXAM_STARTED,
  LANGUAGE_ACCOUNT_LOCK_STORAGE_KEY,
  normalizeLanguageAccountLockPayload,
} from '../utils/languageSync';
import { consumePendingLoginLanguageNotice } from '../utils/auth';
import { buildLanguageContinuationPath, getLanguageFromPathname } from '../utils/languageRouting';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const UPDATE_ERROR_MESSAGE = 'We could not update your language preference. Please try again.';
const INVALID_LANGUAGE_MESSAGE = 'Please choose French or German.';
const SAVED_PROGRESS_LOCKED_MESSAGE =
  'You cannot change the language as you have saved progress. Please use a new account if you wish to practice a new language.';
const LANGUAGE_LABELS = {
  fr: 'French',
  de: 'German',
};

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  changeLanguage: async () => ({ success: false, message: 'Language provider unavailable' }),
  languageLocked: false,
  lockedMessage: UPDATE_ERROR_MESSAGE,
  loading: true,
  hasSelectedLanguage: false,
  shouldRequireLanguageSelection: false,
  languageChangePending: false,
  pendingLanguage: null,
  languageToast: { message: '', severity: 'info' },
  clearLanguageToast: () => {},
  setClientLanguageLock: () => {},
});

const detectLanguageFromLocation = (location) => {
  if (!location) {
    return null;
  }

  const { pathname, search } = location;
  const pathLanguage = getLanguageFromPathname(pathname);

  if (pathLanguage && SUPPORTED_LANGUAGES.includes(pathLanguage)) {
    return pathLanguage;
  }

  if (search) {
    const params = new URLSearchParams(search);
    const langParam = params.get('lang')?.toLowerCase();
    if (langParam && SUPPORTED_LANGUAGES.includes(langParam)) {
      return langParam;
    }
  }

  return null;
};

const updateUserTargetLanguage = async (firebaseUser, targetLanguage, examSettings) => {
  if (!firebaseUser) {
    return { success: false, message: UPDATE_ERROR_MESSAGE };
  }

  try {
    const token = await firebaseUser.getIdToken();
    const response = await axios.put(
      `${API_BASE_URL}/user/profile`,
      { target_lang: targetLanguage, ...examSettings },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return {
      success: true,
      language: normalizeLanguage(response?.data?.target_lang ?? targetLanguage),
    };
  } catch (error) {
    console.error('Failed to update user target language:', error);
    const errorMessage = error.response?.data?.error || UPDATE_ERROR_MESSAGE;
    return { success: false, message: errorMessage };
  }
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, profileLoading, profile, updateProfileLanguage } = useAuth();
  const [language, setLanguage] = useState(() => {
    const storedValue = normalizeLanguage(readLanguagePreference());
    if (storedValue) {
      return storedValue;
    }

    return detectLanguageFromLocation(typeof window === 'undefined' ? null : window.location) ?? DEFAULT_LANGUAGE;
  });
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(() => Boolean(readLanguagePreference()));
  const [initializing, setInitializing] = useState(true);
  const [languageLocked, setLanguageLocked] = useState(false);
  const [lockedMessage, setLockedMessage] = useState('');
  const [localFlowLanguageLocked, setLocalFlowLanguageLocked] = useState(false);
  const [localFlowLockedMessage, setLocalFlowLockedMessage] = useState('');
  const [syncedAccountLanguageLocked, setSyncedAccountLanguageLocked] = useState(false);
  const [syncedAccountLockedMessage, setSyncedAccountLockedMessage] = useState('');
  const [languageChangePending, setLanguageChangePending] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState(null);
  const [languageToast, setLanguageToast] = useState({ message: '', severity: 'info' });
  const [shouldRequireLanguageSelection, setShouldRequireLanguageSelection] = useState(false);
  const currentLanguageRef = useRef(language);
  const resolvedIdentityRef = useRef(null);
  const localFlowLanguageLockedRef = useRef(localFlowLanguageLocked);
  const languageResolutionVersionRef = useRef(0);
  const currentUserUidRef = useRef(user?.uid ?? null);
  const previousAuthSessionKeyRef = useRef(user ? `auth:${user.uid}` : 'guest');
  const pendingProfileLanguageOverrideRef = useRef(null);

  useEffect(() => {
    currentLanguageRef.current = language;
  }, [language]);

  const setClientLanguageLock = useCallback((isLocked, message = '') => {
    setLocalFlowLanguageLocked(isLocked);
    setLocalFlowLockedMessage(isLocked ? message : '');
  }, []);

  const applySyncedAccountLock = useCallback((message = SAVED_PROGRESS_LOCKED_MESSAGE) => {
    setSyncedAccountLanguageLocked(true);
    setSyncedAccountLockedMessage(message);
  }, []);

  const clearSyncedAccountLock = useCallback(() => {
    setSyncedAccountLanguageLocked(false);
    setSyncedAccountLockedMessage('');
  }, []);

  const showLanguageToast = useCallback((message, severity = 'info') => {
    if (!message) {
      return;
    }

    setLanguageToast({ message, severity });
  }, []);

  const clearLanguageToast = useCallback(() => {
    setLanguageToast({ message: '', severity: 'info' });
  }, []);

  const applyBootstrapLanguage = useCallback((value) => {
    const normalized = normalizeLanguage(value) ?? DEFAULT_LANGUAGE;
    setLanguage(normalized);
  }, []);

  const applyExplicitLanguage = useCallback((value, { persistPreference = true } = {}) => {
    const normalized = normalizeLanguage(value);
    if (!normalized) {
      return;
    }

    setLanguage(normalized);
    setHasSelectedLanguage(true);
    if (persistPreference) {
      writeLanguagePreference(normalized);
    }
  }, []);

  const rememberPendingProfileLanguageOverride = useCallback(
    (nextLanguage) => {
      const languageOverride = normalizeLanguage(nextLanguage);
      if (!user?.uid || !languageOverride) {
        return;
      }

      pendingProfileLanguageOverrideRef.current = {
        uid: user.uid,
        language: languageOverride,
      };
    },
    [user?.uid],
  );

  useEffect(() => {
    localFlowLanguageLockedRef.current = localFlowLanguageLocked;
  }, [localFlowLanguageLocked]);

  useEffect(() => {
    currentUserUidRef.current = user?.uid ?? null;
  }, [user]);

  useEffect(() => {
    const authSessionKey = user ? `auth:${user.uid}` : 'guest';

    if (previousAuthSessionKeyRef.current !== authSessionKey) {
      clearSyncedAccountLock();
    }

    previousAuthSessionKeyRef.current = authSessionKey;
  }, [user, clearSyncedAccountLock]);

  useEffect(() => {
    let cancelled = false;
    const identityKey = user?.uid ?? 'guest';

    const determineLanguage = async () => {
      if (authLoading || profileLoading) {
        return;
      }

      if (user && resolvedIdentityRef.current !== identityKey) {
        setInitializing(true);
        setShouldRequireLanguageSelection(false);
      }

      const storedLanguage = normalizeLanguage(readLanguagePreference());
      const urlLanguage = detectLanguageFromLocation(location);
      const languageResolutionVersion = languageResolutionVersionRef.current;

      if (pendingProfileLanguageOverrideRef.current?.uid !== user?.uid) {
        pendingProfileLanguageOverrideRef.current = null;
      }

      if (user) {
        if (!cancelled && languageResolutionVersion === languageResolutionVersionRef.current) {
          const profileLanguage = normalizeLanguage(profile?.targetLang);
          const pendingOverride = pendingProfileLanguageOverrideRef.current;
          if (pendingOverride && profileLanguage === pendingOverride.language) {
            pendingProfileLanguageOverrideRef.current = null;
          }

          const activePendingOverride = pendingProfileLanguageOverrideRef.current;
          const fetchedData = profile
            ? {
                language: activePendingOverride?.language ?? profileLanguage,
                hasSelectedLanguage: activePendingOverride ? true : profile.hasSelectedLanguage,
                hasCustomLists: profile.hasCustomLists,
                hasCustomVocab: profile.hasCustomVocab,
                hasMockExams: profile.hasMockExams,
              }
            : activePendingOverride
              ? {
                  language: activePendingOverride.language,
                  hasSelectedLanguage: true,
                  hasCustomLists: false,
                  hasCustomVocab: false,
                  hasMockExams: false,
                }
              : null;
          const dbHasSelectedLanguage = Boolean(fetchedData?.hasSelectedLanguage && fetchedData?.language);
          const shouldConsiderLoginNotice = consumePendingLoginLanguageNotice();
          const previousLanguage = currentLanguageRef.current;
          const locked =
            Boolean(fetchedData?.hasCustomLists) ||
            Boolean(fetchedData?.hasCustomVocab) ||
            Boolean(fetchedData?.hasMockExams);

          setShouldRequireLanguageSelection(Boolean(fetchedData && !fetchedData.hasSelectedLanguage && !locked));

          if (dbHasSelectedLanguage) {
            if (shouldConsiderLoginNotice && previousLanguage && previousLanguage !== fetchedData.language) {
              const nextLanguageLabel = LANGUAGE_LABELS[fetchedData.language] ?? fetchedData.language.toUpperCase();
              showLanguageToast(`We changed the language to ${nextLanguageLabel} for this account.`, 'info');
            }
            applyExplicitLanguage(fetchedData.language);
          } else {
            applyBootstrapLanguage(urlLanguage ?? DEFAULT_LANGUAGE);
            setHasSelectedLanguage(false);
          }

          setLanguageLocked(locked);

          if (fetchedData?.hasMockExams) {
            setLockedMessage(SAVED_PROGRESS_LOCKED_MESSAGE);
          } else if (fetchedData?.hasCustomVocab) {
            setLockedMessage(SAVED_PROGRESS_LOCKED_MESSAGE);
          } else if (fetchedData?.hasCustomLists) {
            setLockedMessage(SAVED_PROGRESS_LOCKED_MESSAGE);
          } else {
            setLockedMessage('');
          }
        }
      } else if (!cancelled) {
        setShouldRequireLanguageSelection(false);
        if (storedLanguage) {
          applyExplicitLanguage(storedLanguage, { persistPreference: false });
        } else {
          applyBootstrapLanguage(urlLanguage ?? DEFAULT_LANGUAGE);
          setHasSelectedLanguage(false);
        }

        setLanguageLocked(false);
        setLockedMessage('');
      }

      if (!cancelled) {
        resolvedIdentityRef.current = identityKey;
        setInitializing(false);
      }
    };

    determineLanguage();

    return () => {
      cancelled = true;
    };
  }, [authLoading, profileLoading, user, profile, applyExplicitLanguage, applyBootstrapLanguage, location]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.storageArea && event.storageArea !== window.localStorage) {
        return;
      }

      if (event.key === LANGUAGE_KEY || event.key === LEGACY_LANGUAGE_KEY) {
        if (localFlowLanguageLockedRef.current) {
          return;
        }

        const syncedLanguage = normalizeLanguage(event.newValue);
        if (!syncedLanguage || syncedLanguage === currentLanguageRef.current) {
          return;
        }

        languageResolutionVersionRef.current += 1;
        if (user) {
          rememberPendingProfileLanguageOverride(syncedLanguage);
          updateProfileLanguage(syncedLanguage);
        }
        applyExplicitLanguage(syncedLanguage, { persistPreference: false });
        return;
      }

      if (event.key !== LANGUAGE_ACCOUNT_LOCK_STORAGE_KEY) {
        return;
      }

      const payload = normalizeLanguageAccountLockPayload(event.newValue);
      if (!payload || payload.reason !== LANGUAGE_ACCOUNT_LOCK_REASON_MOCK_EXAM_STARTED) {
        return;
      }

      if (!currentUserUidRef.current || payload.uid !== currentUserUidRef.current) {
        return;
      }

      applySyncedAccountLock();
    };

    const handleLocalLanguageAccountLock = (event) => {
      const payload = normalizeLanguageAccountLockPayload(event.detail);
      if (!payload || payload.reason !== LANGUAGE_ACCOUNT_LOCK_REASON_MOCK_EXAM_STARTED) {
        return;
      }

      if (!currentUserUidRef.current || payload.uid !== currentUserUidRef.current) {
        return;
      }

      applySyncedAccountLock();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(LANGUAGE_ACCOUNT_LOCK_CUSTOM_EVENT, handleLocalLanguageAccountLock);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(LANGUAGE_ACCOUNT_LOCK_CUSTOM_EVENT, handleLocalLanguageAccountLock);
    };
  }, [
    applyExplicitLanguage,
    applySyncedAccountLock,
    rememberPendingProfileLanguageOverride,
    updateProfileLanguage,
    user,
  ]);

  useEffect(() => {
    if (authLoading || profileLoading || initializing) {
      return;
    }

    const routeLanguage = detectLanguageFromLocation(location);

    if (!routeLanguage || routeLanguage === language) {
      return;
    }

    if (!hasSelectedLanguage) {
      setLanguage(routeLanguage);
      return;
    }

    const continuationPath = buildLanguageContinuationPath(location, language);
    const currentPath = `${location.pathname}${location.search}${location.hash}`;

    if (continuationPath && continuationPath !== currentPath) {
      navigate(continuationPath, { replace: true });
    }
  }, [authLoading, profileLoading, initializing, location, language, hasSelectedLanguage, user, navigate]);

  const effectiveClientLanguageLocked = localFlowLanguageLocked || syncedAccountLanguageLocked;
  const effectiveClientLockedMessage = localFlowLanguageLocked
    ? localFlowLockedMessage
    : syncedAccountLanguageLocked
      ? syncedAccountLockedMessage
      : '';

  const changeLanguage = useCallback(
    async (requestedLanguage, options = {}) => {
      const { suppressFailureToast = false, examSettings } = options;
      const examSettingsFields = {};
      for (const field of ['targetExamLevel', 'examDate']) {
        const value = examSettings?.[field];
        if (typeof value === 'string' && value.trim()) {
          examSettingsFields[field] = value.trim();
        }
      }

      const failLanguageChange = (message) => {
        const resolvedMessage = message || UPDATE_ERROR_MESSAGE;
        if (!suppressFailureToast) {
          showLanguageToast(resolvedMessage, 'warning');
        }
        return { success: false, message: resolvedMessage };
      };

      if (effectiveClientLanguageLocked) {
        return failLanguageChange(effectiveClientLockedMessage || UPDATE_ERROR_MESSAGE);
      }

      if (languageLocked) {
        return failLanguageChange(lockedMessage);
      }

      const nextLanguage = normalizeLanguage(requestedLanguage);

      if (!nextLanguage) {
        return failLanguageChange(INVALID_LANGUAGE_MESSAGE);
      }

      if (
        nextLanguage === language &&
        hasSelectedLanguage &&
        !shouldRequireLanguageSelection &&
        !Object.keys(examSettingsFields).length
      ) {
        return { success: true, language };
      }

      const shouldShowSuccessToast = hasSelectedLanguage && nextLanguage !== language;

      if (user) {
        setLanguageChangePending(true);
        setPendingLanguage(nextLanguage);

        let result;
        try {
          result = await updateUserTargetLanguage(user, nextLanguage, examSettingsFields);
        } finally {
          setLanguageChangePending(false);
          setPendingLanguage(null);
        }

        if (!result.success) {
          return failLanguageChange(result.message);
        }

        const resolvedLanguage = result.language ?? nextLanguage;
        languageResolutionVersionRef.current += 1;
        setShouldRequireLanguageSelection(false);
        rememberPendingProfileLanguageOverride(resolvedLanguage);
        updateProfileLanguage(resolvedLanguage);
        applyExplicitLanguage(resolvedLanguage);

        if (shouldShowSuccessToast) {
          const nextLanguageLabel = LANGUAGE_LABELS[resolvedLanguage] ?? resolvedLanguage.toUpperCase();
          showLanguageToast(`Language changed to ${nextLanguageLabel}.`, 'success');
        }

        return { success: true, language: resolvedLanguage };
      }

      if (shouldRequireLanguageSelection) {
        setShouldRequireLanguageSelection(false);
      }
      applyExplicitLanguage(nextLanguage);

      if (shouldShowSuccessToast) {
        const nextLanguageLabel = LANGUAGE_LABELS[nextLanguage] ?? nextLanguage.toUpperCase();
        showLanguageToast(`Language changed to ${nextLanguageLabel}.`, 'success');
      }

      return { success: true, language: nextLanguage };
    },
    [
      applyExplicitLanguage,
      effectiveClientLanguageLocked,
      effectiveClientLockedMessage,
      hasSelectedLanguage,
      language,
      languageLocked,
      lockedMessage,
      rememberPendingProfileLanguageOverride,
      showLanguageToast,
      shouldRequireLanguageSelection,
      updateProfileLanguage,
      user,
    ],
  );

  const loading = authLoading || profileLoading || initializing;

  const value = useMemo(
    () => ({
      language,
      changeLanguage,
      languageLocked: languageLocked || effectiveClientLanguageLocked,
      lockedMessage: effectiveClientLanguageLocked ? effectiveClientLockedMessage : lockedMessage,
      loading,
      hasSelectedLanguage,
      shouldRequireLanguageSelection,
      languageChangePending,
      pendingLanguage,
      languageToast,
      clearLanguageToast,
      setClientLanguageLock,
    }),
    [
      language,
      changeLanguage,
      languageLocked,
      effectiveClientLanguageLocked,
      effectiveClientLockedMessage,
      lockedMessage,
      loading,
      hasSelectedLanguage,
      shouldRequireLanguageSelection,
      languageChangePending,
      pendingLanguage,
      languageToast,
      clearLanguageToast,
      setClientLanguageLock,
    ],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
